import React from 'react';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import { i18n } from 'config/locales';
import DateRange from 'components/Form/DateRange';
import NotApplicable from 'components/Form/NotApplicable';
import Textarea from 'components/Form/Textarea';
import Location from 'components/Form/Location';
import Text from 'components/Form/Text';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import { LegalAssociationsMembershipOverthrowItemModel } from 'forms/sections/legal/associations/membershipOverthrow/types';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonScrollIntoView,
  CommonRequired,
  CommonAddressBooks,
} from 'types/props';
import { Dispatch } from 'redux';

interface Props
  extends ValidationElementProps,
  Partial<LegalAssociationsMembershipOverthrowItemModel> {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  className?: string
  scrollIntoView?: CommonScrollIntoView
  required?: CommonRequired
  attribute?: string
  addressBooks?: CommonAddressBooks

  dispatch?: Dispatch
}

export default class MembershipOverthrowItem extends ValidationElement<Props> {
  update = (queue: Partial<LegalAssociationsMembershipOverthrowItemModel>) => {
    this.props.onUpdate({
      Organization: this.props.Organization,
      Address: this.props.Address,
      Dates: this.props.Dates,
      PositionsNotApplicable: this.props.PositionsNotApplicable,
      Positions: this.props.Positions,
      ContributionsNotApplicable: this.props.ContributionsNotApplicable,
      Contributions: this.props.Contributions,
      Reasons: this.props.Reasons,
      ...queue,
    });
  };

  updateOrganization = (values: LegalAssociationsMembershipOverthrowItemModel['Organization']) => {
    this.update({
      Organization: values,
    });
  };

  updateAddress = (values: LegalAssociationsMembershipOverthrowItemModel['Address']) => {
    this.update({
      Address: values,
    });
  };

  updateDates = (values: LegalAssociationsMembershipOverthrowItemModel['Dates']) => {
    this.update({
      Dates: values,
    });
  };

  updatePositionsNotApplicable = (
    values: LegalAssociationsMembershipOverthrowItemModel['PositionsNotApplicable']
  ) => {
    this.update({
      Positions: values.applicable ? this.props.Positions : {},
      PositionsNotApplicable: values,
    });
  };

  updatePositions = (values: LegalAssociationsMembershipOverthrowItemModel['Positions']) => {
    this.update({
      Positions: values,
    });
  };

  updateContributionsNotApplicable = (
    values: LegalAssociationsMembershipOverthrowItemModel['ContributionsNotApplicable']
  ) => {
    this.update({
      Contributions: values.applicable ? this.props.Contributions : {},
      ContributionsNotApplicable: values,
    });
  };

  updateContributions = (
    values: LegalAssociationsMembershipOverthrowItemModel['Contributions']
  ) => {
    this.update({
      Contributions: values,
    });
  };

  updateReasons = (values: LegalAssociationsMembershipOverthrowItemModel['Reasons']) => {
    this.update({
      Reasons: values,
    });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;
    return (
      <div>
        <Field
          attribute={`${attribute}.Organization`}
          title={i18n.t('legal.associations.overthrow.heading.organization')}
          adjustFor="text"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            attribute={`${attribute}.Organization`}
            name="Organization"
            {...this.props.Organization}
            onUpdate={this.updateOrganization}
            onError={this.props.onError}
            className="legal-associations-overthrow-organization"
            required={this.props.required}
          />
        </Field>

        <Field
          attribute={`${attribute}.Address`}
          title={i18n.t('legal.associations.overthrow.heading.address')}
          optional
          help="legal.associations.overthrow.help.address"
          adjustFor="address"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Location
            attribute={`${attribute}.Address`}
            name="Address"
            {...this.props.Address}
            onUpdate={this.updateAddress}
            onError={this.props.onError}
            className="legal-associations-overthrow-address"
            layout={Location.ADDRESS}
            geocode
            addressBooks={this.props.addressBooks}
            addressBook="Organization"
            dispatch={this.props.dispatch}
            required={this.props.required}
          />
        </Field>

        <Field
          attribute={`${attribute}.Dates`}
          title={i18n.t('legal.associations.overthrow.heading.dates')}
          help="legal.associations.overthrow.help.dates"
          adjustFor="daterange"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateRange
            attribute={`${attribute}.Dates`}
            name="Dates"
            {...this.props.Dates}
            minDateEqualTo
            onUpdate={this.updateDates}
            onError={this.props.onError}
            className="legal-associations-overthrow-dates"
            required={this.props.required}
            hideFromDay
            hideToDay
          />
        </Field>

        <Field
          attribute={`${attribute}.Positions`}
          title={i18n.t('legal.associations.overthrow.heading.positions')}
          adjustFor="text"
          scrollIntoView={this.props.scrollIntoView}
        >
          <NotApplicable
            attribute={`${attribute}.PositionsNotApplicable`}
            name="PositionsNotApplicable"
            {...this.props.PositionsNotApplicable}
            className="legal-associations-overthrow-positions-na"
            onUpdate={this.updatePositionsNotApplicable}
            onError={this.props.onError}
            or={i18n.m('legal.associations.overthrow.para.or')}
            label={i18n.t('legal.associations.overthrow.label.noposition')}
            required={this.props.required}
            includeNAConfirmation={Boolean(this.props.Positions?.value)}
          >
            <Text
              attribute={`${attribute}.Positions`}
              name="Positions"
              {...this.props.Positions}
              onUpdate={this.updatePositions}
              onError={this.props.onError}
              className="legal-associations-overthrow-positions"
              required={this.props.required}
            />
          </NotApplicable>
        </Field>

        <Field
          attribute={`${attribute}.Contributions`}
          title={i18n.t('legal.associations.overthrow.heading.contributions')}
          adjustFor="text"
          scrollIntoView={this.props.scrollIntoView}
        >
          <NotApplicable
            attribute={`${attribute}.ContributionsNotApplicable`}
            name="ContributionsNotApplicable"
            {...this.props.ContributionsNotApplicable}
            className="legal-associations-overthrow-contributions-na"
            onUpdate={this.updateContributionsNotApplicable}
            onError={this.props.onError}
            or={i18n.m('legal.associations.overthrow.para.or')}
            label={i18n.t('legal.associations.overthrow.label.nocontribs')}
            required={this.props.required}
            includeNAConfirmation={Boolean(this.props.Contributions?.value)}
          >
            <Text
              name="Contributions"
              attribute={`${attribute}.Contributions`}
              {...this.props.Contributions}
              onUpdate={this.updateContributions}
              onError={this.props.onError}
              className="legal-associations-overthrow-contributions"
              required={this.props.required}
            />
          </NotApplicable>
        </Field>

        <Field
          attribute={`${attribute}.Reasons`}
          title={i18n.t('legal.associations.overthrow.heading.reasons')}
          adjustFor="textarea"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            attribute={`${attribute}.Reasons`}
            name="Reasons"
            {...this.props.Reasons}
            onUpdate={this.updateReasons}
            onError={this.props.onError}
            className="legal-associations-overthrow-reasons"
            required={this.props.required}
          />
        </Field>
      </div>
    );
  }
}

MembershipOverthrowItem.defaultProps = {
  PositionsNotApplicable: { applicable: true },
  ContributionsNotApplicable: { applicable: true },
  onUpdate: () => { },
  onError: (value, arr) => arr,
};
